/**
 * F40 (Wave 2 Track D — admin) — promotion suspend dialog.
 *
 * Composes the F02 `Modal` + `Textarea` primitives. Unlike the heavier
 * `@bravemove/ui-web` `SuspendDialog` (type-to-confirm + reason picker), the F40
 * promotion suspend dialog is the simpler plan shape: a mandatory free-form
 * reason (`SuspendPromotionInputSchema` requires 1..500 chars). The destructive
 * Confirm button stays disabled until the reason is non-empty (min 1 char).
 *
 * Rendered only on the detail page, and only when the actor holds
 * `promotions:moderate` (the read-only finance / support roles never mount it).
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Button, Modal, Textarea } from '@bravemove/ui-web';

const REASON_MAX = 500;

export interface SuspendPromotionDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => void | Promise<void>;
  readonly loading?: boolean;
}

export function SuspendPromotionDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
}: SuspendPromotionDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');

  // Reset the reason whenever the dialog (re)opens — the "form reset on open"
  // pattern used by the ui-web SuspendDialog.
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReason('');
    }
  }, [open]);

  const trimmed = reason.trim();
  const canConfirm = trimmed.length >= 1 && trimmed.length <= REASON_MAX && !loading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    void onConfirm(trimmed);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:promotion.suspend.title', { defaultValue: 'Suspend promotion' })}
      description={t('admin:promotion.suspend.body', {
        defaultValue:
          'Suspending hides the promotion and blocks new redemptions immediately. The lifecycle status is unchanged. Provide a reason for the audit log.',
      })}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {t('promotion:action.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            loading={loading}
            data-testid="promotion-suspend-confirm"
          >
            {t('admin:promotion.suspend.confirm', { defaultValue: 'Suspend promotion' })}
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-1.5">
        <span className="text-[12.5px] font-semibold text-ink">
          {t('admin:promotion.suspend.reasonLabel', { defaultValue: 'Reason for suspension' })}
        </span>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={REASON_MAX}
          placeholder={t('admin:promotion.suspend.reasonPlaceholder', {
            defaultValue: 'Why are you suspending this promotion?',
          })}
          aria-label={t('admin:promotion.suspend.reasonLabel', {
            defaultValue: 'Reason for suspension',
          })}
          data-testid="promotion-suspend-reason"
        />
      </label>
    </Modal>
  );
}

export default SuspendPromotionDialog;
